const BASE_URL = "https://visa.ex.zana.ai/visa-vita-backend";

// ============================================
// TOKEN MANAGEMENT
// ============================================

const getAccessToken = () => localStorage.getItem("visaVitaAccessToken");
const setAccessToken = (token) => localStorage.setItem("visaVitaAccessToken", token);
const clearAccessToken = () => localStorage.removeItem("visaVitaAccessToken");

// ============================================
// GENERIC API FETCHER
// ============================================

const fetchData = async (endpoint, options = {}) => {
  const accessToken = getAccessToken();
  try {
    const headers = {
      ...options.headers,
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const response = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.log(`API Error: ${response.status} - ${errorText}`);

      if (response.status === 401) {
        clearAccessToken();
        throw new Error("Authentication failed. Please login again.");
      }

      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      return data;
    } else {
      const text = await response.text();
      return { success: true, message: text };
    }
  } catch (error) {
    console.log(`Error fetching ${endpoint}:`, error);
    throw error;
  }
};

// ============================================
// AUTHENTICATION
// ============================================

/**
 * Authenticate with VisaVita API
 * POST /api/login
 * 
 * @param {string} username
 * @param {string} password
 * @returns {Promise<Object>}
 */
export const authenticateVisaVita = async (username, password) => {
  try {
    const response = await fetchData('/api/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    });

    if (response.access_token) {
      setAccessToken(response.access_token);
      console.log(`User: ${response.username}, Roles: ${response.roles.join(', ')}`);
      return response;
    } else {
      throw new Error("No access token received");
    }
  } catch (error) {
    console.log(`Authentication failed:`, error);
    throw error;
  }
};

/**
 * Logout and clear access token
 */
export const logoutVisaVita = () => {
  clearAccessToken();
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!getAccessToken();
};

// ============================================
// DATA INGESTION
// ============================================

/**
 * Check ingestion status
 * GET /ingest
 * 
 * @returns {Promise<Object>}
 */
export const checkIngestionStatus = async () => {
  try {
    const data = await fetchData('/ingest');
    return data;
  } catch (error) {
    console.log(`Error checking ingestion status:`, error);
    throw error;
  }
};

/**
 * Upload a FormData body to an import endpoint.
 * The backend answers errors with { success: false, message }, so that message
 * is surfaced instead of the raw "API Error: ..." text.
 */
const postImport = async (endpoint, formData) => {
  try {
    return await fetchData(endpoint, {
      method: 'POST',
      body: formData,
    });
  } catch (error) {
    // fetch rejects with a TypeError when the server can't be reached
    if (error instanceof TypeError) {
      throw new Error(`Could not reach the server (${BASE_URL}). Check that the backend is running.`);
    }
    const jsonStart = error.message.indexOf('{');
    let backendMessage = null;
    try {
      if (jsonStart !== -1) backendMessage = JSON.parse(error.message.substring(jsonStart)).message;
    } catch {
      // Not JSON; keep the original error
    }
    throw backendMessage ? new Error(backendMessage) : error;
  }
};

/**
 * Import Corsano data from ZIP file
 * POST /vita/import/zip
 *
 * Each subfolder of the ZIP is imported as one patient. When an identifier is given,
 * all files are imported for that single patient instead.
 *
 * @param {File} zipFile - ZIP file containing CSV data files
 * @param {string} [identifier] - Optional patient identifier
 * @returns {Promise<Object>} Import result
 */
export const importZipFile = async (zipFile, identifier) => {
  const formData = new FormData();
  formData.append('zipFile', zipFile);
  if (identifier) formData.append('identifier', identifier);

  const response = await postImport('/vita/import/zip', formData);

  if (response.importedPersons) {
    console.log(`Imported ${response.importedPersonsCount} person(s):`);
    response.importedPersons.forEach(person => {
      console.log(`${person.identifier} (ID: ${person.person.id})`);
    });
  }

  return response;
};

/**
 * Import glucose data from a CSV file
 * POST /vita/import/csv
 *
 * @param {File} csvFile - Glucose CSV file (patient ID in the second column)
 * @returns {Promise<Object>} Import summary (prefix, personsCreated, insertedRecords, skippedRows, ptIds)
 */
export const importGlucoseCsv = async (csvFile) => {
  const formData = new FormData();
  formData.append('csvFile', csvFile);
  return postImport('/vita/import/csv', formData);
};

/**
 * Import stress measurement files
 * POST /vita/import/stress
 *
 * Accepts either one ZIP file or several CSV files named <uuid>_<type>.csv
 *
 * @param {File[]} files - One ZIP file, or one or more CSV files
 * @returns {Promise<Object>} Import result
 */
export const importStressFiles = async (files) => {
  const formData = new FormData();
  const zip = files.find(file => file.name.toLowerCase().endsWith('.zip'));
  if (zip) {
    formData.append('zipFile', zip);
  } else {
    files.forEach(file => formData.append('files', file));
  }
  return postImport('/vita/import/stress', formData);
};

// ============================================
// PERSON MANAGEMENT
// ============================================

/**
 * Get all persons
 * GET /vita/persons
 * 
 * @returns {Promise<Object>} List of all persons
 */
export const getAllPersons = async () => {
  try {
    const data = await fetchData('/vita/persons');

    if (Array.isArray(data)) {
      return { success: true, persons: data };
    }
    if (data?.success) {
      return data;
    } else {
      console.log('Failed to fetch persons');
      return { success: false, persons: [] };
    }
  } catch (error) {
    console.log('Error getting all persons:', error);
    throw error;
  }
};

// ============================================
// PERSON IDENTITY MAPPING
// ============================================

/**
 * Get Person by External Identifier
 * GET /vita/mapping/person/{identifier}
 * 
 * @param {string} identifier - External string identifier (from mapping.externalIdentifier)
 * @returns {Promise<Object>} Person data with internal ID
 */
export const getPersonByIdentifier = async (identifier) => {
  try {
    const encodedIdentifier = encodeURIComponent(identifier);
    const data = await fetchData(`/vita/mapping/person/${encodedIdentifier}`);

    if (data?.success) {
      return data;
    } else {
      console.log(`Person lookup failed for identifier: ${identifier}`, data);
      return null;
    }
  }
  catch (error) {
    console.log(`Error getting person for identifier ${identifier}:`, error);
    throw error;
  }
};

/**
 * Get External Identifier by Person ID
 * GET /vita/mapping/identifier/{id}
 * 
 * @param {number} personId - Internal person ID
 * @returns {Promise<Object>} Person data with external identifier
 */
export const getIdentifierByPersonId = async (personId) => {
  try {
    const data = await fetchData(`/vita/mapping/identifier/${personId}`);

    if (data?.success) {
      return data;
    } else {
      console.log(`Identifier lookup failed for person ID: ${personId}`);
      return null;
    }
  } catch (error) {
    console.log(`Error getting identifier for person ID ${personId}:`, error);
    throw error;
  }
};

// ============================================
// MEASUREMENTS RETRIEVAL
// ============================================

/**
 * Get All Measurements by Identifier
 * GET /vita/persons/{identifier}/measurements
 * 
 * @param {string} identifier - External string identifier
 * @param {Object} pagination - Pagination parameters
 * @param {number} pagination.max - Maximum number of records to return (default: 20)
 * @param {number} pagination.offset - Number of records to skip (default: 0)
 * @param {number} [pagination.from] - Only measurements at or after this time (epoch ms)
 * @param {number} [pagination.to] - Only measurements before this time (epoch ms)
 * @returns {Promise<Object>} Complete measurements data
 */
export const getAllMeasurementsByIdentifier = async (identifier, pagination = {}) => {
  try {
    const encodedIdentifier = encodeURIComponent(identifier);

    // Build query parameters for pagination and the optional time window
    const queryParams = new URLSearchParams();
    if (pagination.max !== undefined) queryParams.append('max', pagination.max);
    if (pagination.offset !== undefined) queryParams.append('offset', pagination.offset);
    if (pagination.from !== undefined && pagination.from !== null) queryParams.append('from', pagination.from);
    if (pagination.to !== undefined && pagination.to !== null) queryParams.append('to', pagination.to);

    const queryString = queryParams.toString();
    const endpoint = `/vita/persons/${encodedIdentifier}/measurements${queryString ? `?${queryString}` : ''}`;

    const data = await fetchData(endpoint);

    if (data?.success) {
      // Calculate total measurements from the new structure
      const measurements = data.measurements || {};
      const totals = data.pagination?.totals || {};

      const totalMeasurements = Object.values(totals).reduce((sum, count) => sum + (count || 0), 0);

      const measurementCounts = {
        total: totalMeasurements,
        activities: totals.activities || 0,
        emographies: totals.emographies || 0,
        heartRateVariabilities: totals.heartRateVariabilities || 0,
        temperatures: totals.temperatures || 0,
        bloodPressures: measurements.bloodPressures?.length || 0,
        stressAcclerometer: totals.stressAcclerometer || 0,
        stressAnnotation: totals.stressAnnotation || 0,
        stressBioz: totals.stressBioz || 0,
        stressHeatFlux: totals.stressHeatFlux || 0,
        stressPPG2: totals.stressPPG2 || 0,
        stressPPG3: totals.stressPPG3 || 0,
        stressRRInterval: totals.stressRRInterval || 0,
        stressSelfReport: totals.stressSelfReport || 0
      };

      // console.log(`Measurements retrieved:`, {
      //   identifier: data.identifier,
      //   personId: data.person.id,
      //   totalMeasurements: totalMeasurements,
      //   counts: measurementCounts
      // });      
      return {
        ...data,
        measurementCounts
      };
    } else {
      console.log(`Measurements retrieval failed for identifier: ${identifier}`);
      return null;
    }
  } catch (error) {
    console.log(`Error getting measurements for identifier ${identifier}:`, error);
    throw error;
  }
};

/**
 * Get Measurements by Type and Identifier
 * GET /vita/persons/{identifier}/measurements/{type}
 * 
 * @param {string} identifier - External string identifier
 * @param {string} type - Measurement type (activities, emographies, heartRateVariabilities, bloodPressures, temperatures)
 * @param {Object} pagination - Pagination parameters
 * @param {number} pagination.max - Maximum number of records to return (default: 20)
 * @param {number} pagination.offset - Number of records to skip (default: 0)
 * @returns {Promise<Object>} Specific measurement type data
 */
export const getMeasurementsByTypeAndIdentifier = async (identifier, type, pagination = {}) => {
  // console.log(`Getting ${type} measurements for identifier: ${identifier}`);
  try {
    const encodedIdentifier = encodeURIComponent(identifier);

    // Build query parameters for pagination
    const queryParams = new URLSearchParams();
    if (pagination.max !== undefined) queryParams.append('max', pagination.max);
    if (pagination.offset !== undefined) queryParams.append('offset', pagination.offset);

    const queryString = queryParams.toString();
    const endpoint = `/vita/persons/${encodedIdentifier}/measurements/${type}${queryString ? `?${queryString}` : ''}`;

    const data = await fetchData(endpoint);
    if (data?.success) {
      return data;
    } else {
      console.log("Failed response data:", data);
      return null;
    }
  } catch (error) {
    console.log(`Error getting ${type} measurements for identifier ${identifier}:`, error);
    throw error;
  }
};

/**
 * Get Measurement Statistics by Person ID
 * GET /vita/persons/{id}/measurements/stats
 * 
 * @param {number} personId - Internal person ID
 * @returns {Promise<Object>} Measurement statistics
 */
export const getMeasurementStatsByPersonId = async (personId) => {
  try {
    const data = await fetchData(`/vita/persons/${personId}/measurements/stats`);

    if (data?.success) {
      if (data.statistics) {
        Object.keys(data.statistics).forEach(type => {
          const stats = data.statistics[type];
        });
      }
      return data;
    } else {
      console.log(`Statistics retrieval failed for person ID: ${personId}`);
      return null;
    }
  } catch (error) {
    console.log(`Error getting statistics for person ID ${personId}:`, error);
    throw error;
  }
};

// ============================================
// FUNCTIONS FOR SPECIFIC MEASUREMENT TYPES
// ============================================

export const getActivitiesByIdentifier = (identifier, pagination = {}) =>
  getMeasurementsByTypeAndIdentifier(identifier, 'activities', pagination);

export const getEmographiesByIdentifier = (identifier, pagination = {}) =>
  getMeasurementsByTypeAndIdentifier(identifier, 'emographies', pagination);

export const getHeartRateVariabilitiesByIdentifier = (identifier, pagination = {}) =>
  getMeasurementsByTypeAndIdentifier(identifier, 'heartRateVariabilities', pagination);

export const getBloodPressuresByIdentifier = (identifier, pagination = {}) =>
  getMeasurementsByTypeAndIdentifier(identifier, 'bloodPressures', pagination);

export const getTemperaturesByIdentifier = (identifier, pagination = {}) =>
  getMeasurementsByTypeAndIdentifier(identifier, 'temperatures', pagination);

/**
 * Get Glucose Measurements with TIR Stats
 * GET /vita/persons/{identifier}/measurements
 * 
 * @param {string} identifier - External string identifier
 * @param {Object} options - Additional options
 * @param {string} options.significantField - Field to check for significant changes (hr or glucoseCGM)
 * @param {number} options.threshold - Threshold value for significant changes
 * @param {Object} pagination - Pagination parameters
 * @param {number} pagination.max - Maximum number of records to return (default: 20)
 * @param {number} pagination.offset - Number of records to skip (default: 0)
 * @returns {Promise<Object>} Glucose measurements with TIR stats
 */
export const getGlucoseMeasurements = async (identifier, options = {}, pagination = {}) => {
  console.group('🩺 Glucose Measurements API Call');
  const startTime = performance.now();

  try {
    console.log('📝 Request Parameters:', {
      identifier,
      options,
      pagination,
      timestamp: new Date().toISOString()
    });

    const encodedIdentifier = encodeURIComponent(identifier);
    const queryParams = new URLSearchParams();

    if (pagination.max !== undefined) queryParams.append('max', pagination.max);
    if (pagination.offset !== undefined) queryParams.append('offset', pagination.offset);
    if (pagination.from !== undefined && pagination.from !== null) queryParams.append('from', pagination.from);
    if (pagination.to !== undefined && pagination.to !== null) queryParams.append('to', pagination.to);
    if (options.significantField) queryParams.append('significantField', options.significantField);
    if (options.threshold !== undefined) queryParams.append('threshold', options.threshold);

    const queryString = queryParams.toString();
    const endpoint = `/vita/persons/${encodedIdentifier}/measurements${queryString ? `?${queryString}` : ''}`;

    console.log('📡 API Endpoint:', endpoint);
    console.log('🔍 Query Parameters:', Object.fromEntries(queryParams.entries()));

    const data = await fetchData(endpoint);

    console.log('✅ Response Summary:', {
      success: data?.success,
      measurementsCount: data?.measurements?.length || 0,
      hasTirStats: !!data?.tirStats,
      responseTime: `${(performance.now() - startTime).toFixed(2)}ms`
    });

    if (data?.tirStats) {
      console.log('📊 TIR Stats:', {
        severe_low: `${data.tirStats.severe_low?.toFixed(2)}%`,
        low: `${data.tirStats.low?.toFixed(2)}%`,
        target: `${data.tirStats.target?.toFixed(2)}%`,
        high: `${data.tirStats.high?.toFixed(2)}%`,
        severe_high: `${data.tirStats.severe_high?.toFixed(2)}%`,
        totalDurationMinutes: data.tirStats.totalDurationMinutes,
        totalValidMeasurements: data.tirStats.totalValidMeasurements
      });
    }

    if (data?.success) {
      return data;
    } else {
      console.warn('⚠️ API returned unsuccessful response:', data);
      return null;
    }
  } catch (error) {
    console.error('❌ Error in getGlucoseMeasurements:', {
      message: error.message,
      identifier,
      options,
      timestamp: new Date().toISOString()
    });
    throw error;
  } finally {
    console.log('⏱️ Total Execution Time:', `${(performance.now() - startTime).toFixed(2)}ms`);
    console.groupEnd();
  }
};

// ============================================
// DEMOGRAPHICS
// ============================================

/**
 * Get patient demographics data
 * GET /vita/patients/demographics
 * 
 * @returns {Promise<Object>} Patient demographics data with age and sex distribution
 */
export const getPatientDemographics = async () => {
  try {
    const data = await fetchData('/vita/patients/demographics');

    if (data?.patients) {
      // console.log(`Demographics retrieved: ${data.patients.length} patients`);
      return {
        success: true,
        patients: data.patients,
        totalPatients: data.patients.length
      };
    } else {
      console.log('Failed to fetch demographics data');
      return { success: false, patients: [], totalPatients: 0 };
    }
  } catch (error) {
    console.log('Error getting patient demographics:', error);
    throw error;
  }
};

// ============================================
// PATIENT MANAGEMENT: SUBGROUPS & DELETION
// ============================================

/**
 * Send a JSON request. Errors carry the backend's { message } when it sends one.
 */
const sendJson = async (endpoint, method, body) => {
  try {
    return await fetchData(endpoint, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    const jsonStart = error.message.indexOf('{');
    let backendMessage = null;
    try {
      if (jsonStart !== -1) backendMessage = JSON.parse(error.message.substring(jsonStart)).message;
    } catch {
      // Not JSON; keep the original error
    }
    // Only a plain string message is usable; other backends send message objects
    throw typeof backendMessage === 'string' && backendMessage ? new Error(backendMessage) : error;
  }
};

/** The browser's IANA time zone, used by the backend to split data into calendar days */
export const getBrowserTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/**
 * Get subgroups, optionally of one group
 * GET /vita/subgroups?group={group}
 *
 * @param {string} [group] - VISAVITA_GLUCOSE / VISAVITA_STRESS / VISAVITA_CORSANO (or glucose / stress / corsano)
 * @returns {Promise<Array>} Subgroups with { id, name, group, description, patientCount }
 */
export const getSubgroups = async (group) => {
  const query = group ? `?group=${encodeURIComponent(group)}` : '';
  const data = await fetchData(`/vita/subgroups${query}`);
  return data?.subgroups || [];
};

/**
 * Create a subgroup in a group
 * POST /vita/subgroups
 */
export const createSubgroup = async (group, name, description) => {
  const data = await sendJson('/vita/subgroups', 'POST', { group, name, description });
  return data.subgroup;
};

/**
 * Rename a subgroup or change its description
 * PUT /vita/subgroups/{id}
 */
export const updateSubgroup = async (id, { name, description }) => {
  const data = await sendJson(`/vita/subgroups/${id}`, 'PUT', { name, description });
  return data.subgroup;
};

/**
 * Delete a subgroup; its patients stay in the group
 * DELETE /vita/subgroups/{id}
 */
export const deleteSubgroup = (id) => sendJson(`/vita/subgroups/${id}`, 'DELETE');

/**
 * Put patients into a subgroup, or remove them from their subgroup when subgroupId is null
 * PUT /vita/persons/subgroup
 *
 * @param {string[]} identifiers - External patient identifiers
 * @param {number|null} subgroupId
 */
export const assignPatientsToSubgroup = (identifiers, subgroupId) =>
  sendJson('/vita/persons/subgroup', 'PUT', { identifiers, subgroupId });

/**
 * Permanently delete a patient with all of their measurements
 * DELETE /vita/persons/{identifier}
 *
 * @param {string} identifier - External patient identifier
 * @returns {Promise<Object>} { deletedMeasurements, personDeleted, message }
 */
export const deletePatient = (identifier) =>
  sendJson(`/vita/persons/${encodeURIComponent(identifier)}`, 'DELETE');

// ============================================
// DAILY STATISTICS & COHORT ANALYSIS
// ============================================

/**
 * Days with data for a patient, with min/max/avg of heart rate, steps, respiration and SpO2 per day
 * GET /vita/persons/{identifier}/daily-stats?tz={timeZone}
 *
 * @param {string} identifier - External patient identifier
 * @returns {Promise<Array>} [{ date: 'YYYY-MM-DD', measurementCount, activityCount, activity: { heartRate, steps, respiration, spo2 } }]
 */
export const getDailyStats = async (identifier, timeZone = getBrowserTimeZone()) => {
  const data = await fetchData(
    `/vita/persons/${encodeURIComponent(identifier)}/daily-stats?tz=${encodeURIComponent(timeZone)}`
  );
  return data?.days || [];
};

/**
 * Cohort analysis of one group, optionally narrowed to a subgroup
 * GET /vita/cohort/{group}?subgroupId=&tz=
 *
 * @param {string} group - VISAVITA_GLUCOSE / VISAVITA_STRESS / VISAVITA_CORSANO
 * @param {number|null} [subgroupId]
 * @returns {Promise<Object>} { patientCount, totals, metrics: [...], patients: [...] }
 */
export const getCohortAnalysis = async (group, subgroupId = null, timeZone = getBrowserTimeZone()) => {
  const query = new URLSearchParams({ tz: timeZone });
  if (subgroupId) query.append('subgroupId', subgroupId);
  return fetchData(`/vita/cohort/${encodeURIComponent(group)}?${query.toString()}`);
};

// ============================================
// WORKFLOW HELPER FUNCTIONS
// ============================================

/**
 * Complete person lookup with measurements summary
 * Combines person lookup and measurements count
 * 
 * @param {string} identifier - External string identifier
 * @param {Object} pagination - Pagination parameters for measurements
 * @param {number} pagination.max - Maximum number of records to return (default: 20)
 * @param {number} pagination.offset - Number of records to skip (default: 0)
 * @returns {Promise<Object>} Complete person profile with measurements summary
 */
export const getCompletePersonProfile = async (identifier, pagination = {}) => {
  try {

    // Get person information
    const personData = await getPersonByIdentifier(identifier);
    if (!personData?.success) {
      throw new Error(`Person not found for identifier: ${identifier}`);
    }

    // Get all measurements with pagination
    const measurementsData = await getAllMeasurementsByIdentifier(identifier, pagination);
    if (!measurementsData?.success) {
      // console.warn(`No measurements found for identifier: ${identifier}`);
    }

    // Get statistics if we have the person ID
    let statisticsData = null;
    if (personData.person?.id) {
      try {
        statisticsData = await getMeasurementStatsByPersonId(personData.person.id);
      } catch (error) {
        console.warn(`Could not retrieve statistics: ${error.message}`);
      }
    }

    const profile = {
      success: true,
      identifier,
      person: personData.person,
      measurementCounts: measurementsData?.measurementCounts || {},
      statistics: statisticsData?.statistics || null,
      lastUpdate: new Date().toISOString()
    };

    return profile;

  } catch (error) {
    console.log(`Error getting complete profile for ${identifier}:`, error);
    throw error;
  }
};

/**
 * workflow: Authenticate, check status, and get person profile
 * 
 * @param {string} identifier - External string identifier
 * @param {string} username - Username for authentication
 * @param {string} password - Password for authentication
 * @returns {Promise<Object>} Complete workflow result
 */
export const performCompleteWorkflow = async (identifier, username = null, password = null) => {
  try {
    console.log(`Starting complete VisaVita workflow for identifier: ${identifier}`);

    const workflow = {
      timestamp: new Date().toISOString(),
      identifier,
      steps: {
        authentication: null,
        ingestionStatus: null,
        personProfile: null
      },
      success: false,
      errors: []
    };

    try {
      // Use provided credentials or get from localStorage
      const authUsername = username || localStorage.getItem("visaVitaUsername");
      const authPassword = password || localStorage.getItem("visaVitaPassword");

      if (!authUsername || !authPassword) {
        throw new Error("Credentials not provided and not found in storage. Please sign in first.");
      }

      workflow.steps.authentication = await authenticateVisaVita(authUsername, authPassword);
    } catch (error) {
      workflow.errors.push(`Authentication failed: ${error.message}`);
      throw error;
    }

    try {
      workflow.steps.ingestionStatus = await checkIngestionStatus();
    } catch (error) {
      workflow.errors.push(`Ingestion status check failed: ${error.message}`);
    }

    try {
      workflow.steps.personProfile = await getCompletePersonProfile(identifier);
    } catch (error) {
      workflow.errors.push(`Person profile retrieval failed: ${error.message}`);
      throw error;
    }

    workflow.success = true;
    console.log(`Complete workflow finished successfully for ${identifier}`);
    return workflow;

  } catch (error) {
    console.log(`Workflow failed for ${identifier}:`, error);
    throw error;
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Extract measurements from the new API response structure
 * @param {Object} measurementsData - The measurements data from getAllMeasurementsByIdentifier
 * @param {string} measurementType - The type of measurement to extract
 * @returns {Array} Array of measurements of the specified type
 */
export const extractMeasurementsByType = (measurementsData, measurementType) => {
  if (!measurementsData?.success || !measurementsData?.measurements) {
    return [];
  }

  const measurements = measurementsData.measurements;
  // const measurementKey = measurementType.toLowerCase();

  const keyMap = {
    'activities': 'activities',
    'emographies': 'emographies',
    'heartRateVariabilities': 'heartRateVariabilities',
    'bloodPressures': 'bloodPressures',
    'temperatures': 'temperatures',
    'stressAcclerometer': 'stressAcclerometer',
    'stressAnnotation': 'stressAnnotation',
    'stressBioz': 'stressBioz',
    'stressHeatFlux': 'stressHeatFlux',
    'stressPPG2': 'stressPPG2',
    'stressPPG3': 'stressPPG3',
    'stressRRInterval': 'stressRRInterval',
    'stressSelfReport': 'stressSelfReport'
  };

  const key = keyMap[measurementType] || measurementType;
  return measurements[key] || [];
};

/**
 * Create pagination parameters
 * @param {number} max - Maximum number of records to return
 * @param {number} offset - Number of records to skip
 * @returns {Object} Pagination object
 */
export const createPaginationParams = (max = 20, offset = 0) => ({
  max,
  offset
});

/**
 * Get pagination info from measurements response
 * @param {Object} measurementsData - The measurements data from getAllMeasurementsByIdentifier
 * @returns {Object} Pagination information
 */
export const getPaginationInfo = (measurementsData) => {
  if (!measurementsData?.pagination) {
    return null;
  }

  return {
    current: {
      max: measurementsData.pagination.max || 20,
      offset: measurementsData.pagination.offset || 0
    },
    totals: measurementsData.pagination.totals || {},
    hasMore: (measurementsData.pagination.offset || 0) + (measurementsData.pagination.max || 20) <
      Object.values(measurementsData.pagination.totals || {}).reduce((sum, count) => sum + (count || 0), 0)
  };
};

/**
 * Get current authentication status
 */
export const getAuthenticationStatus = () => {
  const token = getAccessToken();
  return {
    isAuthenticated: !!token,
    hasToken: !!token,
    tokenPreview: token ? `${token.substring(0, 8)}...` : null
  };
};

/**
 * Format measurement statistics for display
 */
export const formatMeasurementStats = (statistics) => {
  if (!statistics) return null;

  const formatted = {};

  Object.keys(statistics).forEach(type => {
    const stats = statistics[type];
    formatted[type] = {
      count: stats.count,
      dateRange: {
        start: new Date(stats.dateRange.min).toLocaleDateString(),
        end: new Date(stats.dateRange.max).toLocaleDateString()
      },
      metrics: Object.keys(stats).filter(key =>
        !['count', 'dateRange'].includes(key)
      ).reduce((acc, key) => {
        acc[key] = stats[key];
        return acc;
      }, {})
    };
  });

  return formatted;
};

/**
 * Validate ZIP file for import
 */
export const validateZipFile = (file) => {
  const errors = [];

  if (!file) {
    errors.push("No file selected");
    return { isValid: false, errors };
  }

  if (!file.name.endsWith('.zip')) {
    errors.push("File must be a ZIP file");
  }

  if (file.size === 0) {
    errors.push("File is empty");
  }

  if (file.size > 100 * 1024 * 1024) { // 100MB limit
    errors.push("File is too large (max 100MB)");
  }

  return {
    isValid: errors.length === 0,
    errors,
    fileInfo: {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: new Date(file.lastModified).toISOString()
    }
  };
};

export default {
  // Authentication
  authenticateVisaVita,
  logoutVisaVita,
  isAuthenticated,
  getAuthenticationStatus,

  // Data Ingestion
  checkIngestionStatus,
  importZipFile,
  importGlucoseCsv,
  importStressFiles,
  validateZipFile,

  // Person Identity Mapping
  getPersonByIdentifier,
  getIdentifierByPersonId,

  // Measurements Retrieval
  getAllMeasurementsByIdentifier,
  getMeasurementsByTypeAndIdentifier,
  getMeasurementStatsByPersonId,
  getGlucoseMeasurements,

  // Demographics
  getPatientDemographics,

  // Patient management
  getSubgroups,
  createSubgroup,
  updateSubgroup,
  deleteSubgroup,
  assignPatientsToSubgroup,
  deletePatient,

  // Daily statistics & cohorts
  getBrowserTimeZone,
  getDailyStats,
  getCohortAnalysis,

  // Convenience Functions
  getActivitiesByIdentifier,
  getEmographiesByIdentifier,
  getHeartRateVariabilitiesByIdentifier,
  getBloodPressuresByIdentifier,
  getTemperaturesByIdentifier,

  // Workflow Helpers
  getCompletePersonProfile,
  performCompleteWorkflow,

  // Utilities
  formatMeasurementStats,
  extractMeasurementsByType,
  createPaginationParams,
  getPaginationInfo
};